import { useState, useEffect, useCallback } from 'react';

import { FullStory } from '@fullstory/browser';

// Extend Window interface to include FullStory
declare global {
    interface Window {
        FS?: {
            event: (eventName: string, properties?: Record<string, any>) => void;
        };
    }
}

/**
 * Form data structure stored in localStorage
 */
interface StoredFormData {
    first_name?: string;
    last_name?: string;
    phone?: string;
    email?: string;
    location?: string;
    description?: string;
    fullUrl?: string;
    urlPath?: string;
}

/**
 * Generic form initial values type
 */
interface FormInitialValues {
    first_name?: string;
    last_name?: string;
    phone?: string;
    email?: string;
    location?: string;
    description?: string;
    [key: string]: any; // Allow additional fields like bodyAreas
}

/**
 * Options for form navigation
 */
interface FormNavigationOptions {
    /** The path to navigate to (e.g., '/specials/estimate') */
    navigationPath: string;
    /** The form field values to store */
    formValues: Record<string, any>;
    /** Optional event name for FullStory tracking */
    eventName?: string;
    /** The localStorage key to use for storing data */
    storageKey?: string;
}

/**
 * Generic function to handle form navigation with data persistence
 * Stores form data in localStorage and navigates to the specified path
 *
 * @param options - Configuration options for form navigation
 */
export function handleFormNavigation(options: FormNavigationOptions): void {
    const { navigationPath, formValues, eventName = 'Form Navigation', storageKey = 'form_data' } = options;

    if (typeof window === 'undefined') {
        console.error('Window is undefined - cannot navigate in SSR');
        return;
    }

    try {
        const formData: Record<string, string | boolean> = {
            first_name: formValues.first_name || '',
            last_name: formValues.last_name || '',
            phone: formValues.phone || '',
            email: formValues.email || '',
            location: formValues.location || '',
            description: formValues.description || '',
            fullUrl: window.location.href,
            urlPath: window.location.pathname,
        };

        window.localStorage.setItem(storageKey, JSON.stringify(formData));

        // FullStory tracking
        if (window.FS) {
            FullStory('trackEvent', {
                name: eventName,
                properties: {
                    toPath: navigationPath,
                    fromFullUrl: window.location.href,
                    fromPath: window.location.pathname,
                },
            });
        }
        // Use window.location.href to ensure full page reload and trigger cloudAmp to run on the new page
        window.location.href = `${navigationPath}?autofill=true`;
    } catch (error) {
        console.error('Error saving form data:', error);
        // Still navigate even if localStorage fails
        window.location.href = `${navigationPath}?autofill=true`;
    }
}

/**
 * Custom hook to pre-populate form data from localStorage when autofill parameter is present
 *
 * @param defaultInitialValues - Default initial values for the form
 * @param storageKey - Optional localStorage key (defaults to 'form_data')
 * @returns Object containing initial form values and the handleFormNavigation function
 */
export function useFormPrePopulation<T extends FormInitialValues>(
    defaultInitialValues: T,
    storageKey: string = 'form_data'
): {
    initialFormValues: T;
    handleFormNavigation: (navigationPath: string, formValues: Record<string, any>, eventName?: string) => void;
} {
    const [initialFormValues, setInitialFormValues] = useState<T>(defaultInitialValues);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        // Check if URL has autofill parameter
        const urlParams = new URLSearchParams(window.location.search);
        const shouldAutofill = urlParams.get('autofill') === 'true';

        if (!shouldAutofill) {
            // No autofill param, use default empty form
            return;
        }

        // autofill=true is present, try to get data from localStorage
        const storedData = window.localStorage.getItem(storageKey);

        if (!storedData) {
            // No data in localStorage, silently show empty form
            return;
        }

        try {
            const parsedData: StoredFormData = JSON.parse(storedData);

            // Pre-populate form with stored data (excluding fullUrl and urlPath)
            setInitialFormValues({
                ...defaultInitialValues,
                first_name: parsedData.first_name || '',
                last_name: parsedData.last_name || '',
                phone: parsedData.phone || '',
                email: parsedData.email || '',
                location: parsedData.location || '',
                description: parsedData.description || '',
            } as T);

            // FullStory tracking with fullUrl and urlPath
            if (window.FS) {
                FullStory('trackEvent', {
                    name: 'Estimate Form Pre-populated',
                    properties: {
                        cameFromPath: parsedData.urlPath || '',
                        cameFromFullUrl: parsedData.fullUrl || '',
                        autofill: true,
                    },
                });
            }
        } catch (error) {
            console.error('Error parsing stored form data:', error);
            // Silent failure - show empty form with default values
        }
    }, [storageKey]); // Only re-run if storageKey changes

    // Create a memoized wrapper for handleFormNavigation that uses the storageKey
    const handleNavigation = useCallback(
        (navigationPath: string, formValues: Record<string, any>, eventName?: string) => {
            handleFormNavigation({
                navigationPath,
                formValues,
                eventName,
                storageKey,
            });
        },
        [storageKey]
    );

    return {
        initialFormValues,
        handleFormNavigation: handleNavigation,
    };
}

export default useFormPrePopulation;
